/**
 * Collapsible product copy with a Read more / Read less toggle.
 *
 * Height mode (default) clamps the content to --ac-read-more-height and fades its last lines.
 * Copy that would only hide a line or two is left open.
 * Paragraph mode keeps any opening bold or heading lines and the first body paragraph, and hides the rest.
 * Elements marked data-read-more-footer, such as a closing link, are hidden with the rest but
 * never count as "more" on their own.
 *
 * Without JavaScript the toggle stays hidden and the full copy shows.
 */
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const MIN_HIDDEN = 64;
const DURATION = 250;
const EASING = 'cubic-bezier(0.33, 1, 0.68, 1)';
const HEADING = 'h1, h2, h3, h4, h5, h6';

/** @param {Element} element */
const hasContent = (element) =>
  element.textContent.trim() !== '' || element.querySelector('img, video, iframe, table') !== null;

/** @param {Element} element */
const isLeadIn = (element) => {
  if (element.matches(HEADING) || !hasContent(element)) return true;
  const bold = element.querySelector('strong, b');
  return bold !== null && bold.textContent.trim() === element.textContent.trim();
};

class AcReadMore extends HTMLElement {
  /** @type {HTMLElement | null} */
  content = null;
  /** @type {HTMLButtonElement | null} */
  toggle = null;
  /** @type {HTMLElement[]} */
  rest = [];
  /** @type {ResizeObserver | null} */
  observer = null;
  /** @type {Animation | null} */
  animation = null;
  expanded = false;

  connectedCallback() {
    if (this.dataset.enhanced) return;
    this.content = this.querySelector('[data-read-more-content]');
    this.toggle = this.querySelector('[data-read-more-toggle]');
    if (!this.content || !this.toggle) return;
    this.dataset.enhanced = 'true';
    this.toggle.addEventListener('click', this.#onClick);

    if (this.dataset.mode === 'paragraph') {
      this.#setUpParagraphs();
    } else {
      this.observer = new ResizeObserver(() => this.#measure());
      this.observer.observe(this);
      this.#measure();
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  get #isParagraphMode() {
    return this.dataset.mode === 'paragraph';
  }

  #setUpParagraphs() {
    if (!this.content || !this.toggle) return;
    const blocks = [...this.content.children].flatMap((element) =>
      element.matches('.metafield-rich_text_field') ? [...element.children] : [element]
    );
    const first = blocks.findIndex((element) => !isLeadIn(element));
    if (first === -1) return;
    const rest = /** @type {HTMLElement[]} */ (blocks.slice(first + 1));
    if (!rest.some((element) => !element.hasAttribute('data-read-more-footer') && hasContent(element))) return;
    this.rest = rest;
    this.#setRestHidden(true);
    this.toggle.hidden = false;
  }

  #measure() {
    if (this.expanded || this.animation || !this.content || !this.toggle) return;
    // Hidden, for example the desktop section on a phone: measure once it is shown.
    if (!this.content.getClientRects().length) return;
    this.dataset.state = 'collapsed';
    const { clientHeight, scrollHeight } = this.content;
    // Only fold away more than a few lines; a couple of hidden lines are not worth a click.
    const collapsible = scrollHeight - clientHeight > MIN_HIDDEN;
    this.dataset.state = collapsible ? 'collapsed' : 'open';
    this.toggle.hidden = !collapsible;
  }

  /** @param {boolean} hidden */
  #setRestHidden(hidden) {
    for (const element of this.rest) element.hidden = hidden;
  }

  #onClick = () => {
    if (this.expanded) {
      this.#collapse();
    } else {
      this.#expand();
    }
  };

  async #expand() {
    if (!this.content) return;
    const from = this.content.offsetHeight;
    this.expanded = true;
    if (this.#isParagraphMode) {
      this.#setRestHidden(false);
    } else {
      this.dataset.state = 'expanded';
    }
    this.#updateToggle();
    await this.#animate(from, this.content.offsetHeight);
  }

  async #collapse() {
    if (!this.content || !this.toggle) return;
    const from = this.content.offsetHeight;
    this.expanded = false;
    this.#updateToggle();

    // Measure the collapsed height, then keep the full copy laid out while the height closes over it.
    if (this.#isParagraphMode) {
      this.#setRestHidden(true);
      const to = this.content.offsetHeight;
      this.#setRestHidden(false);
      await this.#animate(from, to);
      // A second click may have reopened it mid-animation.
      if (this.expanded) return;
      this.#setRestHidden(true);
    } else {
      this.dataset.state = 'collapsed';
      const to = this.content.offsetHeight;
      this.dataset.state = 'collapsing';
      await this.#animate(from, to);
      if (this.expanded) return;
      this.dataset.state = 'collapsed';
    }

    this.toggle.scrollIntoView({ block: 'nearest' });
  }

  /**
   * @param {number} from
   * @param {number} to
   */
  async #animate(from, to) {
    const { content } = this;
    this.animation?.cancel();
    if (!content || reducedMotion.matches || from === to) return;
    content.style.overflow = 'hidden';
    const animation = content.animate([{ maxHeight: `${from}px` }, { maxHeight: `${to}px` }], {
      duration: DURATION,
      easing: EASING,
    });
    this.animation = animation;
    try {
      await animation.finished;
    } catch {
      // Cancelled by a second click, which has already applied its own state.
    }
    if (this.animation !== animation) return;
    this.animation = null;
    content.style.removeProperty('overflow');
  }

  #updateToggle() {
    const { toggle } = this;
    if (!toggle) return;
    toggle.setAttribute('aria-expanded', String(this.expanded));
    const label = toggle.querySelector('[data-read-more-label]');
    const text = this.expanded ? toggle.dataset.labelLess : toggle.dataset.labelMore;
    if (label && text) label.textContent = text;
  }
}

if (!customElements.get('ac-read-more')) {
  customElements.define('ac-read-more', AcReadMore);
}
